import { useCallback } from 'react';

import { useInfiniteQuery, useQueryClient } from '@northladder/services';

import { AUTO_ASSESSMENTS_API } from '../autoAssessmentsAPIClient';
import { IAutoAssessmentsRequestsListPayload } from '../types';

/**
 * To track the auto assessments listing key.
 */
export const FETCH_ALL_ASSESSMENTS_REQUESTS_KEY =
  'fetch-all-assessments-requests-key';

interface IUseAllAutoAssessmentsRequestsListQueryOptions {
  payload: IAutoAssessmentsRequestsListPayload;
  identifier?: string;
}

/**
 * It fetches the auto assessments requests list data, also takes in the payload
 * for filters, search key mapping, sorting, pagination etc.
 */
export const useAutoAssessmentsRequestsListQuery = ({
  payload,
  identifier,
}: IUseAllAutoAssessmentsRequestsListQueryOptions) => {
  const queryClient = useQueryClient();

  const { limit, search, sort, status } = payload;

  const {
    data,
    isPending,
    isError,
    error,
    isPaused,
    refetch,
    hasNextPage,
    fetchNextPage,
    isFetching,
    isFetchingNextPage,
  } = useInfiniteQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: [FETCH_ALL_ASSESSMENTS_REQUESTS_KEY, limit, search, sort, status],
    networkMode: 'always',
    initialPageParam: 1,
    queryFn: ({ pageParam = 1 }) =>
      AUTO_ASSESSMENTS_API.fetchAllAssessmentsRequests(
        {
          ...payload,
          page: pageParam,
        },
        identifier
      ),
    select: (response) => response,
    getNextPageParam: (lastPage, allPages) => {
      const nextPage = allPages.length + 1;

      const currentAssetCount = allPages.length * limit;

      return currentAssetCount < lastPage.total ? nextPage : undefined;
    },
    refetchInterval: 10000,
    staleTime: 0,
  });

  const handleRefetch = useCallback(() => {
    refetch();

    // TODO: @yatendra, Why is the query invalidating its own cache. Refetch should already do this.
    // NOTE: This logic has been extracted from `AssessmentResultsList` component
    // apps/gradepro-mobile/src/features/home/containers/AssessmentList/AssessmentResultsList.tsx
    queryClient.invalidateQueries({
      queryKey: [
        FETCH_ALL_ASSESSMENTS_REQUESTS_KEY,
        limit,
        sort,
        search,
        status,
      ],
    });
  }, [refetch, queryClient, limit, sort, search, status]);

  return {
    data,
    isPending,
    isError,
    error,
    isPaused,
    refetch: handleRefetch,
    hasNextPage,
    fetchNextPage,
    isFetching,
    isFetchingNextPage,
  };
};
